/**
 * Gestion de la couverture du cache par intervalles
 * Permet de ne charger que les "trous" manquants au lieu de tout recharger
 */

import { addDays, minDate, maxDate } from './dateTime';
import { cacheGet, cacheSet, emitCacheUpdated } from './cache';

// Clé de stockage et version pour la coverage
export const COVERAGE_KEY = 'activity/coverage';
export const COVERAGE_VERSION = 2;

/**
 * @typedef {Object} CoveredSpan
 * @property {string} from - Date de début (YYYY-MM-DD)
 * @property {string} to - Date de fin (YYYY-MM-DD)
 * @property {string} [etag] - ETag pour If-None-Match
 * @property {number} fetchedAt - Timestamp du fetch
 */

/**
 * @typedef {Object} Coverage
 * @property {CoveredSpan[]} spans - Liste des intervalles couverts
 * @property {number} version - Version du format
 */

/**
 * Ajoute un nouveau span et fusionne les chevauchements
 * NOTE: Ne s'appuie plus sur etag pour fusionner (etag hétérogènes)
 * @param {Coverage} coverage - Couverture actuelle
 * @param {CoveredSpan} newSpan - Nouveau span à ajouter
 * @returns {Coverage} Couverture mise à jour
 */
export function addSpan(coverage, newSpan) {
  const spans = [...(coverage?.spans || []), newSpan]
    .sort((a, b) => a.from.localeCompare(b.from));
  
  if (spans.length === 0) {
    return { version: COVERAGE_VERSION, spans: [newSpan] };
  }
  
  const merged = [];
  let current = spans[0];
  
  for (let i = 1; i < spans.length; i++) {
    const next = spans[i];
    
    // Vérifier si chevauchement ou adjacent (à 1 jour près)
    const currentEnd = addDays(current.to, 1);
    if (next.from <= currentEnd) {
      // Fusionner - on garde le fetchedAt le plus récent
      // NOTE: on n'essaie plus de "fusionner" des ETag hétérogènes
      current = {
        from: current.from,
        to: maxDate(current.to, next.to),
        fetchedAt: Math.max(current.fetchedAt, next.fetchedAt)
      };
    } else {
      // Pas de chevauchement, passer au suivant
      merged.push(current);
      current = next;
    }
  }
  merged.push(current);
  
  return { version: COVERAGE_VERSION, spans: merged };
}

/**
 * Trouve les "trous" (périodes non couvertes) dans la couverture
 * @param {Coverage} coverage - Couverture actuelle
 * @param {string} from - Date de début demandée (YYYY-MM-DD)
 * @param {string} to - Date de fin demandée (YYYY-MM-DD)
 * @returns {CoveredSpan[]} Liste des trous à charger
 */
export function findHoles(coverage, from, to) {
  if (!coverage.spans || coverage.spans.length === 0) {
    // Pas de couverture → tout est un trou
    return [{ from, to, fetchedAt: 0 }];
  }
  
  const holes = [];
  let current = from;
  
  for (const span of coverage.spans) {
    // Si le span commence après notre position actuelle
    if (current <= to && span.from > current) {
      // Il y a un trou avant ce span
      const holeTo = minDate(addDays(span.from, -1), to);
      if (holeTo >= current) {
        holes.push({
          from: current,
          to: holeTo,
          fetchedAt: 0
        });
      }
    }
    
    // Avancer la position actuelle
    if (span.to >= current) {
      current = addDays(span.to, 1);
    }
    
    // Si on a dépassé la fin demandée, on arrête
    if (current > to) break;
  }
  
  // Vérifier s'il reste un trou après tous les spans
  if (current <= to) {
    holes.push({ from: current, to, fetchedAt: 0 });
  }
  
  return holes;
}

/**
 * Vérifie si un span est périmé (cache trop ancien)
 * @param {CoveredSpan} span - Span à vérifier
 * @param {number} maxAge - Âge max en millisecondes (défaut: 5 min)
 * @returns {boolean} True si périmé
 */
export function isStale(span, maxAge = 5 * 60 * 1000) {
  return Date.now() - span.fetchedAt > maxAge;
}

/**
 * Nettoie les spans périmés de la couverture
 * @param {Coverage} coverage - Couverture actuelle
 * @param {number} maxAge - Âge max en millisecondes
 * @returns {Coverage} Couverture nettoyée
 */
export function removeStaleSpans(coverage, maxAge = 5 * 60 * 1000) {
  const freshSpans = coverage.spans.filter(span => !isStale(span, maxAge));
  return { ...coverage, spans: freshSpans };
}

/**
 * Crée une couverture vide
 * @returns {Coverage} Couverture vide
 */
export function createEmptyCoverage() {
  return {
    spans: [],
    version: COVERAGE_VERSION
  };
}

/**
 * Lit la coverage depuis le cache
 * @returns {Coverage} Coverage actuelle ou vide si absente
 */
export function readCoverage() {
  return cacheGet(COVERAGE_KEY, createEmptyCoverage());
}

/**
 * Écrit la coverage dans le cache et émet événement
 * @param {Coverage} cov - Coverage à écrire
 */
export function writeCoverage(cov) {
  cacheSet(COVERAGE_KEY, cov);
  emitCacheUpdated(COVERAGE_KEY);
}

/**
 * Vérifie si une période est entièrement couverte
 * @param {Coverage} coverage - Couverture actuelle
 * @param {string} from - Date de début (YYYY-MM-DD)
 * @param {string} to - Date de fin (YYYY-MM-DD)
 * @returns {boolean} True si entièrement couvert
 */
export function isFullyCovered(coverage, from, to) {
  const holes = findHoles(coverage, from, to);
  return holes.length === 0;
}

/**
 * Convertit l'ancien format lastFetch en Coverage
 * Migration legacy lastFetch -> coverage v2
 * @param {Object} lastFetch - Ancien format {from, to, at}
 * @returns {Coverage} Nouveau format Coverage
 */
export function migrateLegacyLastFetch(lastFetch) {
  if (!lastFetch || !lastFetch.from || !lastFetch.to) {
    return createEmptyCoverage();
  }
  
  return {
    spans: [{
      from: lastFetch.from,
      to: lastFetch.to,
      fetchedAt: lastFetch.at || Date.now()
    }],
    version: COVERAGE_VERSION
  };
}
